/**
 * The `/saved` header (SPEC `saved-posts` § Chrome).
 *
 * The `/feed` pill verbatim — back chevron + title on the left, controls on the right — with one
 * addition: a Search box, because on this screen the words you are looking for are often your own.
 * It matches the post text, the author, your note and your tag names (#66 `q`).
 *
 * The sub-row is `meta.summaryDisplay`, rendered verbatim (§12.5).
 */

import { Link } from 'react-router';
import type { ViewRangeState } from '@lw/shared';
import {
  CalendarIcon,
  ChevronLeftIcon,
  FilterIcon,
  Menu,
  SearchIcon,
} from '../../components';
import { HOME_PATH } from '../../app/routes';
import { formatDateCompact } from '../../lib/datetime';
import { RangePanel } from '../view/RangePanel';
import { SortMenu } from '../view/SortMenu';
import { RANGE_PRESET_LABEL, type ResolvedRange, type ViewSort } from '../view/viewState';

/** Action-button glyph size — matches the view header's, since it is the same pill. */
const ACTION_ICON = 13;

export type SavedHeaderProps = {
  /** `undefined` while the first page is in flight; the sub-row stays blank rather than guessing. */
  summaryDisplay: string | undefined;
  search: string;
  onSearchChange: (next: string) => void;
  sort: ViewSort;
  onSortChange: (next: ViewSort) => void;
  range: ViewRangeState;
  resolvedRange: ResolvedRange;
  onRangeChange: (next: ViewRangeState) => void;
  timezone: string;
  now: number;
  /** Only under the drawer breakpoint: the sidebar is unmounted and needs a way in. */
  onOpenFilters: (() => void) | null;
  filterCount: number;
};

export function SavedHeader({
  summaryDisplay,
  search,
  onSearchChange,
  sort,
  onSortChange,
  range,
  resolvedRange,
  onRangeChange,
  timezone,
  now,
  onOpenFilters,
  filterCount,
}: SavedHeaderProps) {
  const rangeLabel =
    range.preset === 'custom'
      ? `${formatDateCompact(resolvedRange.from, timezone)} – ${formatDateCompact(resolvedRange.to, timezone)}`
      : RANGE_PRESET_LABEL[range.preset];

  return (
    <>
      <div className="view-header">
        <div className="view-header__pill">
          <div className="view-header__left">
            <Link className="icon-btn" to={HOME_PATH} aria-label="Back to profiles">
              <ChevronLeftIcon />
            </Link>
            <span className="view-header__name">Saved Posts</span>
          </div>

          <div className="view-header__actions">
            <div className="sb-search saved-search">
              <SearchIcon size={13} />
              <input
                className="sb-search__input"
                type="search"
                value={search}
                placeholder="Search saved…"
                aria-label="Search saved posts"
                onChange={(event) => onSearchChange(event.target.value)}
              />
            </div>

            {onOpenFilters === null ? null : (
              <button
                type="button"
                className="table-action table-action--icon"
                aria-label="Filters"
                onClick={onOpenFilters}
              >
                <FilterIcon size={ACTION_ICON} />
                {filterCount > 0 ? <span className="count-badge">{filterCount}</span> : null}
              </button>
            )}

            {/* `isGroup` opens the Author sort: a collection is always multi-author. */}
            <SortMenu sort={sort} onChange={onSortChange} isGroup />

            <Menu
              label={
                <>
                  <CalendarIcon size={ACTION_ICON} />
                  <span className="table-action__label">{rangeLabel}</span>
                </>
              }
              align="right"
              panelClassName="menu__panel--pad"
            >
              {() => (
                <RangePanel
                  range={range}
                  resolved={resolvedRange}
                  onChange={onRangeChange}
                  timezone={timezone}
                  now={now}
                />
              )}
            </Menu>
          </div>
        </div>
      </div>

      <div className="view-sub feed-sub">{summaryDisplay ?? ' '}</div>
    </>
  );
}

export default SavedHeader;
